import React, { useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import ExplanationBox from '../shared/ExplanationBox';
import GuardrailWarning from '../shared/GuardrailWarning';
import Tooltip from '../shared/Tooltip';

const Step3_MethodologicalChoice = () => {
  const { data, updateData, warnings, explanations } = useWizard();
  
  const choices = [
    { 
      value: 'qual', 
      label: 'Mono-method Qualitative',
      description: 'Use one qualitative method only (e.g., interviews OR observations)',
      bestFor: 'Deep understanding, meaning-making, exploratory research'
    },
    { 
      value: 'quant', 
      label: 'Mono-method Quantitative',
      description: 'Use one quantitative method only (e.g., survey OR experiment)',
      bestFor: 'Testing hypotheses, measuring variables, statistical analysis'
    },
    { 
      value: 'multi_qual', 
      label: 'Multi-method Qualitative',
      description: 'Multiple qualitative methods (e.g., interviews AND observations)',
      bestFor: 'Triangulation within qualitative paradigm, rich qualitative data'
    },
    { 
      value: 'multi_quant', 
      label: 'Multi-method Quantitative',
      description: 'Multiple quantitative methods (e.g., survey AND experiments)',
      bestFor: 'Triangulation within quantitative paradigm, comprehensive measurement'
    },
    { 
      value: 'mixed', 
      label: 'Mixed Methods',
      description: 'Combine qualitative AND quantitative methods with integration',
      bestFor: 'Complex questions, complementary insights, comprehensive understanding'
    }
  ];
  
  return (
    <div>
      <h2 className="step-title">Step 5: Methodological Choice</h2>
      <p className="step-description">
        What type of evidence will answer your question best?
        <Tooltip text="This choice determines whether you use qualitative, quantitative, or mixed methods. Multi-method means using multiple approaches within one paradigm (qual or quant). Mixed methods means integrating both paradigms." />
      </p>
      
      {explanations.recommendation && (
        <ExplanationBox title="Recommended based on your data type">
          <p>{explanations.recommendation}</p>
        </ExplanationBox>
      )}
      
      <div className="form-group">
        <label className="form-label">Select your methodological choice:</label>
        <div className="option-cards">
          {choices.map(choice => (
            <div 
              key={choice.value}
              className={`option-card ${data.methodologicalChoice === choice.value ? 'selected' : ''}`}
              onClick={() => updateData('methodologicalChoice', choice.value)}
            >
              <div className="option-header">
                <input 
                  type="radio"
                  name="methodologicalChoice"
                  value={choice.value}
                  checked={data.methodologicalChoice === choice.value}
                  onChange={(e) => updateData('methodologicalChoice', e.target.value)}
                />
                <span className="option-title">{choice.label}</span>
              </div>
              <p className="option-description" style={{ marginBottom: '0.75rem' }}>
                {choice.description}
              </p>
              <p className="option-description" style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                <strong>Best for:</strong> {choice.bestFor}
              </p>
            </div>
          ))}
        </div>
      </div>
      
      {data.methodologicalChoice === 'mixed' && (
        <div style={{ marginTop: '2rem' }}>
          <ExplanationBox title="Mixed Methods Integration Plan" type="warning">
            <p>Mixed methods require careful planning of how qual and quant components will be integrated. Please specify:</p>
          </ExplanationBox>
          
          <div className="form-group">
            <label className="form-label">Priority / Weight:</label>
            <select
              value={data.mixedMethodsPriority || ''}
              onChange={(e) => updateData('mixedMethodsPriority', e.target.value)}
            >
              <option value="">Select priority...</option>
              <option value="QUAL_quan">QUAL → quan (qualitative dominant)</option>
              <option value="QUAN_qual">QUAN → qual (quantitative dominant)</option>
              <option value="QUAL_QUAN">QUAL + QUAN (equal weight)</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Timing:</label>
            <select
              value={data.mixedMethodsTiming || ''}
              onChange={(e) => updateData('mixedMethodsTiming', e.target.value)}
            >
              <option value="">Select timing...</option>
              <option value="concurrent">Concurrent (collect both at same time)</option>
              <option value="sequential">Sequential (one after the other)</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Integration Point:</label>
            <select
              value={data.mixedMethodsIntegration || ''}
              onChange={(e) => updateData('mixedMethodsIntegration', e.target.value)}
            >
              <option value="">Select integration point...</option>
              <option value="design">At design stage (one informs the other)</option>
              <option value="methods">At methods stage (connected sampling/data)</option>
              <option value="interpretation">At interpretation (merge findings)</option>
              <option value="all_stages">At all stages (fully integrated)</option>
            </select>
          </div>
        </div>
      )}
      
      <div className="info-box">
        <div className="info-title">💡 Key Distinction</div>
        <div className="info-content">
          <p><strong>Multi-method</strong> = Multiple methods within ONE paradigm (e.g., interviews + focus groups = multi-method qual)</p>
          <p style={{ marginTop: '0.5rem' }}>
            <strong>Mixed methods</strong> = Combining BOTH qual AND quant with intentional integration
          </p>
        </div>
      </div>
      
      {warnings.length > 0 && warnings.map((warning, idx) => (
        <GuardrailWarning key={idx} type={warning.type} title={warning.type === 'error' ? 'Required' : 'Note'}>
          {warning.message}
        </GuardrailWarning>
      ))}
      
      <NavigationButtons />
    </div>
  );
};

export default Step3_MethodologicalChoice;
